import { CountryCode } from '~/enums';
import { Language, RootState } from '~/store/types/sharedTypes';
import { useDispatch, useSelector } from 'react-redux';
import {
  setLanguage as _setLanguage,
  setMarket as _setMarket,
} from '~/store/slices/intl.slice';
import { useUpdateCompanyLanguageMutation } from '~/store/apis/endpoints/myQredApi/companies/companies.api';
import { setSelectedCompany } from '~/store/slices/accounts.slice';
import { Company, User } from '~/models/accounts/accounts';
import { useLazyGetAccountQuery } from '~/store/apis/endpoints/myQredApi/accounts/accounts.api';
import useLogout from '~/hooks/useLogout';

interface UseAccountReturn {
  init(): void;
  isInitialized: boolean;
  hasNoCompany: boolean;
  updateSelectedCompany(company: Company): void;
  setMarket(market: CountryCode): void;
  setLanguage(language: Language): void;
  selectedCompany: Company | undefined;
  user: User;
  companies: Company[];
  market: CountryCode;
  language: Language;
}

export const useAccount = (): UseAccountReturn => {
  const dispatch = useDispatch();
  const logout = useLogout();

  const { market, language } = useSelector((state: RootState) => state.intl);
  const { user, companies, selectedCompany } = useSelector(
    (state: RootState) => state.account
  );

  const isSelectedCompanyInitialized = !!selectedCompany?.id;

  const [
    getAccountTrigger,
    { isSuccess: getAccountIsSuccess },
  ] = useLazyGetAccountQuery();
  const [updateCompanyLanguage] = useUpdateCompanyLanguageMutation();

  const setMarket = (_market: CountryCode) => {
    dispatch(_setMarket(_market));
  };

  const setLanguage = (_language: Language) => {
    dispatch(_setLanguage(_language));

    // Without a company there is nothing on the backend to update.
    if (!selectedCompany) {
      return;
    }

    updateCompanyLanguage({
      id: selectedCompany.id,
      languageCode: _language,
    });
  };

  const setCompany = (company: Company) => {
    localStorage.setItem('@qred/selectedCompanyId', company.id.toString());
    dispatch(setSelectedCompany(company));
  };

  const init = () => {
    getAccountTrigger()
      .unwrap()
      .catch((err) => {
        if (err.status === 401) {
          logout();
        }
      });
  };

  return {
    market,
    language,
    setMarket,
    setLanguage,
    init,
    updateSelectedCompany: setCompany,
    selectedCompany,
    user,
    companies,
    isInitialized: getAccountIsSuccess && isSelectedCompanyInitialized,
    hasNoCompany: getAccountIsSuccess && !isSelectedCompanyInitialized,
  };
};

/**
 * Returns the selected company for components rendered behind
 * AuthenticatedRoute, which only renders its children once a company exists
 * (users without any company are shown a no-access screen instead).
 * Throwing here flags a component that was rendered outside that guarantee.
 */
export const useSelectedCompany = (): Company => {
  const { selectedCompany } = useAccount();

  if (!selectedCompany) {
    throw new Error('useSelectedCompany used without a selected company');
  }

  return selectedCompany;
};
